import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CognitoUser } from '@aws-amplify/auth';
import { RoleRight } from '@dep/common/interfaces';
import { UserWithRoles } from '@dep/common/user-api/types/user.type';
import { NGXLogger } from 'ngx-logger';
import { Subscription } from 'rxjs';

import { ProfileManagerPopupComponent } from '../popups/profile-manager/profile-manager.component';
import { BasketService } from '../shop/services/basket.service';
import { UserService } from '../users/services/user.service';

import { RolesManagerPopupComponent } from '@dep/frontend/popups/roles-manager/roles-manager.component';
import { PopupService, PopupSize } from '@dep/frontend/services/popup.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  public isLoggedIn = false;
  public username?: string;
  private sessionSub$?: Subscription;
  private routerSub$?: Subscription;
  private orderProductsCountSub$?: Subscription;
  /** Amount of order products that are currently in the basket. */
  public orderProductsCount = 0;
  /** `true` if anything in the header is currently being processed that should block further UI interactions */
  private isProcessing = false;
  public isStartPage = false;
  /**
   * Indicates whether the "My Profile" option should be visible.
   * Only users with the DEP.mkh_user or DR_Product:Information_Platform roles can see it,
   * since the associated popup only allows configuration of the start page preference.
   */
  public hasMkhRole = false;
  private user?: UserWithRoles | null;

  constructor(
    private readonly router: Router,
    private readonly logger: NGXLogger,
    private readonly userService: UserService,
    private readonly popupService: PopupService,
    private readonly basketService: BasketService,
  ) { }

  public ngOnInit(): void {
    // If session changes/is updated, re-init.
    this.sessionSub$ = this.userService.sessionObservable$.subscribe((cognitoUser) => {
      void this.init(cognitoUser);
    });

    this.orderProductsCountSub$ = this.basketService.orderProductsCountSubject.subscribe((orderProductsCount) => {
      this.orderProductsCount = orderProductsCount;
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Checks if the current route is '/start'.
        this.isStartPage = event.url === '/start';
      }
    });
  }

  public ngOnDestroy(): void {
    this.sessionSub$?.unsubscribe();
    this.routerSub$?.unsubscribe();
    this.orderProductsCountSub$?.unsubscribe();
  }

  private async init(cognitoUser: CognitoUser | null): Promise<void> {
    this.isLoggedIn = !!cognitoUser;

    this.user = await this.userService.getCurrentUser();
    this.username = this.user?.username.replace('DaimlerSSO_', '');

    // Check if the user has the MAR20X Knowledge Hub role assigned.
    this.hasMkhRole = !!this.user?.roles.some((role) => role.rights.includes(RoleRight.WEBKNOWLEDGE_READ));
  }

  public openRolesManager(): void {
    if (this.isProcessing) {
      this.logger.warn('Header is still processing, cannot open roles manager');
      return;
    }

    this.isProcessing = true;

    if (!this.user) {
      this.logger.error('Cannot open roles manager because user not set');
      this.isProcessing = false;
      return;
    }

    this.popupService.open(
      RolesManagerPopupComponent,
      {
        size: PopupSize.LARGE,
        customValues: {
          userFirstname: this.user.firstname,
          userStates: JSON.stringify(this.user.states),
        },
        closeable: true,
      },
      {
        okButton: () => {
          this.userService.clearUserCache();
        },
      },
    );

    this.isProcessing = false;
  }

  public openProfileManager(): void {
    this.popupService.open(
      ProfileManagerPopupComponent,
      {
        size: PopupSize.LARGE,
      },
    );
  }

  public async logout(): Promise<void> {
    this.userService.logout();
    await this.router.navigate(['login']);
  }

  public async navigateToBasket(): Promise<void> {
    this.logger.info('Navigating to basket');
    await this.router.navigate(['shop/basket']);
  }
}
